import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { adminApi, getErrorMessage } from "../utils/api";
import { Users, Calendar, Clock, BarChart3, Plus, Settings, Loader2, ListChecks, History, ShieldAlert, Trash2, Edit3, X } from 'lucide-react';
import toast from 'react-hot-toast';

const StatCard = ({ label, value, icon: Icon, color, bg }) => (
    <div className="p-6 bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
        <div className="flex items-center justify-between">
            <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">{label}</p>
                <h3 className="text-3xl font-black text-slate-900 tracking-tighter">{value}</h3>
            </div>
            <div className={`${bg.replace('bg-', 'bg-')}/10 ${color.replace('text-', 'text-')} p-3 rounded-xl`}>
                <Icon size={20} />
            </div>
        </div>
    </div>
);

const DeploymentForm = ({ form, setForm, handleCreateSlot }) => (
    <section className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm relative overflow-hidden">
        <div className="absolute top-0 right-0 w-48 h-48 bg-violet-50 rounded-full blur-3xl -mr-24 -mt-24"></div>
        <div className="relative z-10">
            <div className="flex items-center gap-3 mb-6">
                <Plus className="text-violet-600" size={20} />
                <h3 className="text-2xl font-black text-slate-900 tracking-tight">Init Window</h3>
            </div>
            <form onSubmit={handleCreateSlot} className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Exam Label</label>
                    <input type="text" value={form.examName} onChange={(e) => setForm({ ...form, examName: e.target.value })} required className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 outline-none focus:border-violet-600 transition-all font-bold placeholder-slate-400 text-sm" placeholder="e.g. Finals 2026" />
                </div>
                <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Target Date</label>
                    <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 outline-none focus:border-violet-600 transition-all font-bold text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Start</label>
                        <input type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} required className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 outline-none focus:border-violet-600 transition-all font-bold text-sm" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">End</label>
                        <input type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} required className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 outline-none focus:border-violet-600 transition-all font-bold text-sm" />
                    </div>
                </div>
                <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Cycle Dur. (m)</label>
                    <input type="number" value={form.slotDuration} onChange={(e) => setForm({ ...form, slotDuration: e.target.value })} required min="1" className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 outline-none focus:border-violet-600 transition-all font-bold text-sm" />
                </div>
                <div className="md:col-span-2 pt-2">
                    <button type="submit" className="w-full py-4 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-bold transition-all shadow-lg shadow-violet-500/10 active:scale-95 text-xs uppercase tracking-widest">
                        Initialize Deployment
                    </button>
                </div>
            </form>
        </div>
    </section>
);

const RegistryTable = ({ slots, fullRegistry, selectedSlot, setSelectedSlot, handleRemoveStudent, setEditingSlot, handleToggleStatus, handleDeleteSlot }) => (
    <section className="space-y-6">
        <div className="flex items-center justify-between px-2">
            <div className="flex items-center gap-4">
                <ListChecks className="text-violet-600" size={20} />
                <h3 className="text-2xl font-black text-slate-900 tracking-tight">Unit Registry</h3>
            </div>
            {!fullRegistry && (
                <Link to="/admin/registry" className="text-xs font-bold text-violet-600 uppercase tracking-widest hover:text-slate-900 transition-colors">
                    Full Manifest
                </Link>
            )}
        </div>
        <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-slate-50/50 border-b border-slate-100">
                        <tr>
                            <th className="px-8 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest">Operation & Date</th>
                            <th className="px-8 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest">Capacitance</th>
                            <th className="px-8 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest">Command</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {Object.values(slots.reduce((acc, slot) => {
                            const key = `${slot.examName}_${new Date(slot.date).toISOString().split('T')[0]}`;
                            if (!acc[key]) acc[key] = { id: key, name: slot.examName, date: slot.date, slots: [], total: 0, rem: 0 };
                            acc[key].slots.push(slot);
                            acc[key].total += slot.maxCapacity;
                            acc[key].rem += slot.remainingCapacity;
                            return acc;
                        }, {})).slice(0, fullRegistry ? undefined : 3).map((group) => (
                            <React.Fragment key={group.id}>
                                <tr className="bg-white hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setSelectedSlot(selectedSlot?._id === group.slots[0]._id ? null : group.slots[0])}>
                                    <td className="px-8 py-5">
                                        <p className="font-bold text-slate-900 text-base">{group.name}</p>
                                        <p className="text-xs font-bold text-violet-600 uppercase tracking-widest mt-0.5">{new Date(group.date).toLocaleDateString()}</p>
                                    </td>
                                    <td className="px-8 py-5"><p className="text-xs font-bold text-slate-400">{group.slots.length} Units • {group.total - group.rem} / {group.total} Locked</p></td>
                                    <td className="px-8 py-5">
                                        <span className="text-xs font-bold uppercase tracking-widest text-violet-600">
                                            {selectedSlot?._id === group.slots[0]._id ? 'Collapse' : 'Inspect'}
                                        </span>
                                    </td>
                                </tr>
                                {selectedSlot?._id === group.slots[0]._id && group.slots.map(slot => (
                                    <tr key={slot._id} className="bg-slate-50/50">
                                        <td className="px-10 py-3 border-l-2 border-violet-500">
                                            <div className="flex flex-col">
                                                <p className="text-xs font-bold text-slate-900">{slot.startTime} - {slot.endTime}</p>
                                                {slot.bookings?.[0] && (
                                                    <div className="flex items-center gap-2 mt-1 px-2 py-0.5 bg-violet-600/5 rounded-lg w-fit border border-violet-500/10">
                                                        <Users size={10} className="text-violet-600" />
                                                        <span className="text-[10px] font-bold text-violet-700 uppercase tracking-widest truncate max-w-[120px]">{slot.bookings[0].studentName}</span>
                                                        <button onClick={(e) => { e.stopPropagation(); handleRemoveStudent(slot.bookings[0].bookingId) }} className="text-[9px] font-bold text-rose-600 uppercase hover:underline ml-1">Revoke</button>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-8 py-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-lg ${slot.remainingCapacity === 0 ? 'bg-rose-500/10 text-rose-600' : 'bg-teal-500/10 text-teal-600'}`}>{slot.remainingCapacity} / {slot.maxCapacity} Free</span></td>
                                        <td className="px-8 py-3">
                                            <div className="flex gap-4 text-slate-400">
                                                <button onClick={(e) => { e.stopPropagation(); setEditingSlot(slot) }} className="hover:text-violet-600 transition-colors"><Edit3 size={14} /></button>
                                                <button onClick={(e) => { e.stopPropagation(); handleToggleStatus(slot._id) }} className="hover:text-amber-500 transition-colors"><Settings size={14} /></button>
                                                <button onClick={(e) => { e.stopPropagation(); handleDeleteSlot(slot._id) }} className="hover:text-rose-600 transition-colors"><Trash2 size={14} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    </section>
);

const AuditFeed = ({ auditLogs, fullLogs }) => (
    <section className="bg-white rounded-3xl border border-slate-200 p-8 h-full shadow-sm">
        <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
                <History className="text-violet-600" size={20} />
                <h3 className="text-2xl font-black text-slate-900 tracking-tight">Audit Logs</h3>
            </div>
            {!fullLogs && (
                <Link to="/admin/logs" className="text-xs font-bold text-slate-400 uppercase tracking-widest hover:text-slate-900 transition-colors">
                    View All
                </Link>
            )}
        </div>
        <div className="space-y-6">
            {auditLogs.slice(0, fullLogs ? undefined : 10).map((log) => (
                <div key={log._id} className="flex gap-4 p-4 hover:bg-slate-50 rounded-2xl transition-all">
                    <div className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${log.status === 'SUCCESS' ? 'bg-teal-500 shadow-[0_0_8px_rgba(20,184,166,0.2)]' : log.status === 'CANCELLED' ? 'bg-amber-500' : 'bg-rose-500'}`} />
                    <div className="flex-grow">
                        <p className="text-sm font-bold text-slate-900 leading-tight">
                            {log.studentId?.name || 'User'} <span className="text-slate-300 mx-1">•</span> <span className="text-violet-600 font-bold text-xs">{log.slotId?.examName || 'Entity'}</span>
                        </p>
                        <div className="flex justify-between items-center mt-1">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{new Date(log.createdAt).toLocaleTimeString()} • {log.status}</p>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </section>
);

const TeacherRequests = ({ requests, handleReview }) => (
    <section className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
            <ShieldAlert className="text-violet-600" size={20} />
            <h3 className="text-2xl font-black text-slate-900 tracking-tight">Upgrade Requests</h3>
        </div>
        <div className="space-y-4">
            {requests.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No pending requests.</p>
            ) : (
                requests.map(req => (
                    <div key={req._id} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                        <div>
                            <p className="text-sm font-bold text-slate-900">{req.user?.name}</p>
                            <p className="text-[10px] text-slate-400 uppercase tracking-widest">{req.user?.email}</p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => handleReview(req._id, 'approve')} className="px-3 py-1.5 bg-teal-500 text-white text-[10px] font-bold uppercase rounded-lg hover:bg-teal-600 transition-all">Approve</button>
                            <button onClick={() => handleReview(req._id, 'reject')} className="px-3 py-1.5 bg-rose-500 text-white text-[10px] font-bold uppercase rounded-lg hover:bg-rose-600 transition-all">Reject</button>
                        </div>
                    </div>
                ))
            )}
        </div>
    </section>
);

const AdminDashboard = ({ user, fullRegistry = false, fullLogs = false }) => {
    const [slots, setSlots] = useState([]);
    const [auditLogs, setAuditLogs] = useState([]);
    const [teacherRequests, setTeacherRequests] = useState([]);
    const [stats, setStats] = useState({ totalStudents: 0, activeSlots: 0, totalBookings: 0, failedAttempts: 0 });
    const [loading, setLoading] = useState(true);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [editingSlot, setEditingSlot] = useState(null);

    const [form, setForm] = useState({
        examName: "",
        date: "",
        startTime: "",
        endTime: "",
        maxCapacity: "1",
        slotDuration: "30",
    });

    const fetchData = async () => {
        try {
            setLoading(true);
            const [slotsRes, logsRes, statsRes, requestsRes] = await Promise.all([
                adminApi.getSlots(),
                adminApi.getAuditLogs(),
                adminApi.getStats(),
                adminApi.getTeacherRequests()
            ]);
            setSlots(slotsRes.data);
            setAuditLogs(logsRes.data);
            setStats(statsRes.data);
            setTeacherRequests(requestsRes);
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleCreateSlot = async (e) => {
        e.preventDefault();
        try {
            const res = await adminApi.createSlot(form);
            setForm({ examName: "", date: "", startTime: "", endTime: "", maxCapacity: "1", slotDuration: "30" });
            fetchData();
            toast.success(res.message || "Environment initialized");
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const handleToggleStatus = async (id) => {
        try {
            await adminApi.toggleSlot(id);
            fetchData();
            toast.success("State synchronized");
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const handleDeleteSlot = async (id) => {
        if (!window.confirm("CRITICAL: Deleting this window will purge all associated bookings. Continue?")) return;
        try {
            await adminApi.deleteSlot(id);
            fetchData();
            toast.success("Window purged");
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const handleUpdateSlot = async (e) => {
        e.preventDefault();
        try {
            await adminApi.updateSlot(editingSlot._id, editingSlot);
            setEditingSlot(null);
            fetchData();
            toast.success("Manifest updated");
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const handleRemoveStudent = async (bookingId) => {
        if (!window.confirm("CRITICAL: This will immediately revoke student enrollment and free the spot. Continue?")) return;
        try {
            await adminApi.removeBooking(bookingId);
            toast.success("Enrollment revoked");
            fetchData();
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const handleReviewRequest = async (id, action) => {
        try {
            await adminApi.reviewTeacherRequest(id, { action });
            fetchData();
            toast.success(`Request ${action}d`);
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px]">
                <Loader2 className="animate-spin text-violet-600 mb-2" size={24} />
                <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Secure Boot in Progress...</p>
            </div>
        );
    }

    const statCards = [
        { label: 'Total Entities', value: stats.totalStudents, icon: Users, color: 'text-violet-600', bg: 'bg-violet-500' },
        { label: 'Active Windows', value: stats.activeSlots, icon: Calendar, color: 'text-teal-600', bg: 'bg-teal-500' },
        { label: 'Secured Spots', value: stats.totalBookings, icon: Clock, color: 'text-blue-600', bg: 'bg-blue-500' },
        { label: 'Alerts', value: stats.failedAttempts, icon: ShieldAlert, color: 'text-rose-600', bg: 'bg-rose-500' },
    ];







    return (
        <div className="space-y-10 w-full px-6 lg:px-12 py-10 bg-slate-50 min-h-screen text-slate-900">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h2 className="text-4xl font-black tracking-tighter text-slate-900">{user?.name.split(' ')[0]}'s Hub.</h2>
                    <p className="text-slate-500 mt-1.5 font-bold uppercase tracking-widest text-xs">Infrastructure Control Node • {user?.email}</p>
                </div>
                <button onClick={fetchData} className="px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold uppercase tracking-widest text-violet-600 hover:bg-slate-50 transition-all flex items-center gap-2 shadow-sm">
                    <BarChart3 size={16} /> Heartbeat
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {statCards.map((stat, index) => <StatCard key={index} {...stat} />)}
            </div>

            <div className={`grid grid-cols-1 ${(!fullRegistry && !fullLogs) ? 'lg:grid-cols-3' : ''} gap-12`}>
                <div className={(!fullRegistry && !fullLogs) ? 'lg:col-span-2 space-y-12' : 'w-full space-y-12'}>
                    {!fullRegistry && !fullLogs && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <DeploymentForm form={form} setForm={setForm} handleCreateSlot={handleCreateSlot} />
                            <TeacherRequests requests={teacherRequests} handleReview={handleReviewRequest} />
                        </div>
                    )}
                    {!fullLogs && (
                        <RegistryTable
                            slots={slots}
                            fullRegistry={fullRegistry}
                            selectedSlot={selectedSlot}
                            setSelectedSlot={setSelectedSlot}
                            handleRemoveStudent={handleRemoveStudent}
                            setEditingSlot={setEditingSlot}
                            handleToggleStatus={handleToggleStatus}
                            handleDeleteSlot={handleDeleteSlot}
                        />
                    )}
                </div>
                {!fullRegistry && <AuditFeed auditLogs={auditLogs} fullLogs={fullLogs} />}
            </div>

            {editingSlot && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <section className="bg-white border border-slate-200 rounded-3xl p-8 w-full max-w-md shadow-2xl">
                        <div className="flex justify-between items-center mb-8">
                            <h3 className="text-2xl font-black text-slate-900 tracking-tight">Modify Node</h3>
                            <button onClick={() => setEditingSlot(null)} className="text-slate-400 hover:text-slate-900 transition-colors"><X size={20} /></button>
                        </div>
                        <form onSubmit={handleUpdateSlot} className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Entity Name</label>
                                <input type="text" value={editingSlot.examName} onChange={(e) => setEditingSlot({ ...editingSlot, examName: e.target.value })} className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-900 outline-none focus:border-violet-600 text-sm" />
                            </div>
                            <div className="flex gap-4 pt-2">
                                <button type="button" onClick={() => setEditingSlot(null)} className="flex-1 py-3.5 bg-slate-100 text-slate-500 font-bold rounded-xl text-xs uppercase tracking-widest hover:bg-slate-200 transition-all">Abort</button>
                                <button type="submit" className="flex-1 py-3.5 bg-violet-600 text-white font-bold rounded-xl text-xs uppercase tracking-widest shadow-lg shadow-violet-500/10 hover:bg-violet-700 transition-all">Overwrite</button>
                            </div>
                        </form>
                    </section>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
